import React, { useState } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { MdPushPin } from 'react-icons/md';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 2, 1, 2), // 16px left padding for alignment
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.tertiary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}));

const PinButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  opacity: 0,
  transition: 'opacity 0.2s',
  '&:hover': {
    backgroundColor: 'rgba(77, 166, 133, 0.08)',
    color: theme.palette.secondaryNew.main,
  },
  '.MuiListItem-root:hover &': {
    opacity: 1,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

/**
 * Sortable Pin Item
 * Individual pinned data set with drag-and-drop support
 * Supports regular routes, matched subviews, and recipe execution pins
 */
function SortablePinItem({ route, isSelected, onNavigate, onUnpin }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: route.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isRecipePin = route.type === 'recipe-pin';

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
      secondaryAction={
        <Tooltip title="Unpin" placement="right">
          <PinButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onUnpin(route.id);
            }}
          >
            <MdPushPin size={16} />
          </PinButton>
        </Tooltip>
      }
    >
      <ListItemButton
        selected={isSelected}
        onClick={(e) => onNavigate(route.path, route, e)}
        sx={{
          px: 2, // 16px padding left and right
          py: 1, // Vertical padding
          borderLeft: '3px solid transparent',
          alignItems: 'flex-start',
          position: 'relative',
          backgroundColor: isRecipePin ? 'rgba(77, 166, 133, 0.02)' : 'transparent',
          '&.Mui-selected': {
            backgroundColor: 'rgba(77, 166, 133, 0.08)',
            borderColor: isRecipePin ? 'primary.main' : 'secondaryNew.main',
            '&:hover': {
              backgroundColor: 'rgba(77, 166, 133, 0.12)',
            },
          },
          '&:hover': {
            backgroundColor: isRecipePin ? 'rgba(77, 166, 133, 0.04)' : undefined,
          },
        }}
      >
        {/* Drag handle - invisible area on the left */}
        <Box
          {...attributes}
          {...listeners}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 12, // 12px drag area on the left
            cursor: 'grab',
            touchAction: 'none',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        />
        <ListItemText
          sx={{
            my: 0, // Remove default vertical margin
          }}
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: isSelected ? 600 : 500,
                color: isSelected
                  ? (isRecipePin ? 'primary.main' : 'secondaryNew.main')
                  : 'text.primary',
                lineHeight: 1.4,
              }}
            >
              {route.label}
            </Typography>
          }
          secondary={
            route.sublabel ? (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mt: 0.25,
                  lineHeight: 1.3,
                }}
              >
                {route.sublabel}
              </Typography>
            ) : null
          }
        />
      </ListItemButton>
    </ListItem>
  );
}

/**
 * Pinned Routes Component
 *
 * User's favorite routes and recipes for quick access.
 *
 * Features:
 * - Max 10 pins total (routes + recipes combined)
 * - Drag-to-reorder
 * - Pin/unpin toggle on hover
 * - Empty state with helpful guidance
 * - Visual differentiation for recipe pins (beaker icon)
 * - Two-line layout for recipes (name + last run summary)
 * - Recipe pins navigate to latest execution (handled by parent)
 */
export default function PinnedRoutes({
  routes = [],
  onNavigate,
  onUnpin,
  onReorder,
  currentPath = '',
  currentHash = '',
  maxPins = 10,
}) {
  const [localRoutes, setLocalRoutes] = useState(routes);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Update local routes when prop changes
  React.useEffect(() => {
    setLocalRoutes(routes);
  }, [routes]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = localRoutes.findIndex((r) => r.id === active.id);
      const newIndex = localRoutes.findIndex((r) => r.id === over.id);

      const newOrder = arrayMove(localRoutes, oldIndex, newIndex);
      setLocalRoutes(newOrder);

      if (onReorder) {
        onReorder(newOrder.map((r) => r.id));
      }
    }
  };

  const handleNavigate = (path, route, event) => {
    if (onNavigate) {
      onNavigate(path, route, event);
    }
  };

  const handleUnpin = (routeId) => {
    if (onUnpin) {
      onUnpin(routeId);
    }
  };

  // Show empty state if no pinned routes
  if (localRoutes.length === 0) {
    return (
      <Box>
        <SectionHeader>
          <SectionTitle>PINNED</SectionTitle>
        </SectionHeader>
        <EmptyState>
          <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
            Pin your favorite routes and recipes
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            for quick access
          </Typography>
        </EmptyState>
      </Box>
    );
  }

  const currentLocation = `${currentPath}${currentHash}`;
  const selectedViewPin = localRoutes.find(
    (route) => route.type === 'route-view-pin' && route.path === currentLocation
  );

  return (
    <Box>
      <SectionHeader>
        <SectionTitle>
          PINNED ({localRoutes.length}/{maxPins})
        </SectionTitle>
      </SectionHeader>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localRoutes.map((r) => r.id)}
          strategy={verticalListSortingStrategy}
        >
          <List dense disablePadding>
            {localRoutes.map((route) => (
              <SortablePinItem
                key={route.id}
                route={route}
                isSelected={route.type === 'route-view-pin'
                  ? route.path === currentLocation
                  : (!selectedViewPin || selectedViewPin.parentRouteId !== route.id)
                    && (currentPath === route.path || currentPath.startsWith(route.path + '/'))}
                onNavigate={handleNavigate}
                onUnpin={handleUnpin}
              />
            ))}
          </List>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
